import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Loader2, ArrowLeft, ShieldOff, RefreshCw } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { fmtDateTime } from '@/lib/utils';
import CaseLeftPane from '@/components/admin/cases/CaseLeftPane';
import CaseActivityStream from '@/components/admin/cases/CaseActivityStream';
import CaseComposer from '@/components/admin/cases/CaseComposer';
import CaseResolutionBar from '@/components/admin/cases/CaseResolutionBar';
import {
  CASE_STATUS_LABELS,
  CASE_STATUS_STYLES,
  priorityMeta,
} from '@/components/admin/cases/caseConstants';

// The dedicated case workspace. Three panes: what the case is (left), the
// conversation and the reply box (centre), and the tools that close it out
// (right). Below xl the panes stack, with the conversation first.
export default function AdminCaseWorkspace() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { user, isLoadingAuth } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['case-workspace', id],
    queryFn: async () => {
      const res = await base44.functions.invoke('caseWorkspace', { caseId: id });
      if (res.data?.error) throw new Error(res.data.error);
      return res.data;
    },
    enabled: !!id && user?.role === 'admin',
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['case-workspace', id] });

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data?.case) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-white/60 text-sm mb-4">
            {error?.message || 'This case could not be loaded.'}
          </p>
          <Link
            to="/admin/crm"
            className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> BACK TO CASES
          </Link>
        </div>
      </div>
    );
  }

  const c = data.case;
  const priority = priorityMeta(c.priority);

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="px-4 lg:px-8 py-6 border-b border-white/5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <Link
              to="/admin/crm"
              className="inline-flex items-center gap-2 text-white/40 hover:text-white text-[10px] font-bold tracking-widest mb-3 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> BACK TO CASES
            </Link>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-primary text-xs font-bold tracking-widest">
                {c.reference_code}
              </span>
              <span
                className={`text-[9px] font-bold tracking-widest px-2 py-1 border ${
                  CASE_STATUS_STYLES[c.status] || 'border-white/10 text-white/40'
                }`}
              >
                {(CASE_STATUS_LABELS[c.status] || c.status || '—').toUpperCase()}
              </span>
              <span
                className={`flex items-center gap-1.5 text-[9px] font-bold tracking-widest px-2 py-1 border ${priority.ring} ${priority.text}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`} />
                {priority.label.toUpperCase()}
              </span>
              {c.snooze_until && new Date(c.snooze_until) > new Date() && (
                <span className="text-[9px] font-bold tracking-widest px-2 py-1 border border-white/15 text-white/50">
                  SNOOZED
                </span>
              )}
            </div>
            <h1 className="text-white font-black text-xl md:text-2xl uppercase mt-3 break-words">
              {c.subject || 'No subject'}
            </h1>
            <p className="text-white/40 text-xs mt-1">
              {c.customer_name || 'Unknown customer'}
              {c.customer_email ? ` · ${c.customer_email}` : ''}
              {c.last_activity_at ? ` · last activity ${fmtDateTime(c.last_activity_at)}` : ''}
            </p>
          </div>

          <button
            onClick={refresh}
            className="flex items-center gap-2 border border-white/10 hover:border-primary/50 text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-3 py-2 transition-colors shrink-0"
          >
            <RefreshCw className="w-3.5 h-3.5" /> REFRESH
          </button>
        </div>
      </section>

      <section className="px-4 lg:px-8 py-6">
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
          <div className="xl:col-span-3 order-2 xl:order-1">
            <CaseLeftPane data={data} onDone={refresh} />
          </div>

          <div className="xl:col-span-6 order-1 xl:order-2 space-y-5">
            <CaseActivityStream activities={data.activities} />
            <CaseComposer caseData={c} bookingRef={data.linkedBooking?.id} onDone={refresh} />
          </div>

          <div className="xl:col-span-3 order-3">
            <CaseResolutionBar data={data} onDone={refresh} />
          </div>
        </div>
      </section>
    </div>
  );
}